import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Mail,
  Loader2,
  User,
  Sparkles,
  RefreshCw,
  Copy,
  Check,
  ExternalLink,
  Clock,
  Send,
  FileText,
  Inbox,
  Flame,
  Calendar,
  BarChart3,
  DollarSign,
  Eye,
  MousePointer,
  ArrowLeft,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  ActionBackButton,
  LeadInfoCard,
  ActionPageHeader,
  ActionStatsGrid,
  SectionHeader,
  ActionTabs,
  EmptyState,
} from "../../components/admin/ai-actions";

const API = "/api";

export default function AIEmailPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailHistory, setEmailHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("cold_intro");
  const [activeTab, setActiveTab] = useState("drafts");
  
  const token = localStorage.getItem("admin_token");

  const emailTemplates = [
    { id: "cold_intro", label: "Cold Intro", icon: "👋", color: "text-yellow-400 border-yellow-500/30 bg-yellow-500/10" },
    { id: "follow_up", label: "Follow-Up", icon: "📅", color: "text-blue-400 border-blue-500/30 bg-blue-500/10" },
    { id: "demo_invite", label: "Demo Invite", icon: "📊", color: "text-green-400 border-green-500/30 bg-green-500/10" },
    { id: "roi_breakdown", label: "ROI Breakdown", icon: "💰", color: "text-orange-400 border-orange-500/30 bg-orange-500/10" },
  ];

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLead();
    fetchEmailHistory();
  }, [leadId, token]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
      } else {
        toast.error("Lead not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/email-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailHistory(data.email_history || []);
      }
    } catch (error) {
      console.error("Failed to fetch email history:", error);
    }
  };

  const generateEmail = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/admin/crm/draft-email?lead_id=${leadId}&template=${selectedTemplate}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentEmail({
          subject: data.subject || "Follow-up from Unizar",
          body: data.email_draft || "",
          to: lead.email,
          generated_at: new Date().toISOString(),
          template: selectedTemplate
        });
        toast.success("Email draft generated!");
        fetchEmailHistory();
      } else {
        toast.error("Failed to generate email");
      }
    } catch (error) {
      toast.error("Network error - please try again");
    } finally {
      setGenerating(false);
    }
  };

  const copyEmail = () => {
    const fullEmail = `To: ${currentEmail.to}\nSubject: ${currentEmail.subject}\n\n${currentEmail.body}`;
    navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    toast.success("Email copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const openInGmail = () => {
    const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(currentEmail.to)}&su=${encodeURIComponent(currentEmail.subject)}&body=${encodeURIComponent(currentEmail.body)}`;
    window.open(gmailUrl, '_blank');
  };

  const openInOutlook = () => {
    const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(currentEmail.to)}&subject=${encodeURIComponent(currentEmail.subject)}&body=${encodeURIComponent(currentEmail.body)}`;
    window.open(outlookUrl, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate stats
  const getDraftedCount = () => emailHistory.length;
  const getSentCount = () => emailHistory.filter(e => e.status === "sent").length;
  const getOpenedCount = () => emailHistory.filter(e => e.opened).length;
  const getOpenRate = () => {
    const sent = getSentCount();
    if (sent === 0) return 0;
    return Math.round((getOpenedCount() / sent) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <button
            onClick={() => navigate(`/admin/leads/${leadId}`)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to {lead?.first_name}'s Profile</span>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div className="text-right">
              <h1 className="text-xl font-bold text-white font-[Manrope]">AI Email Composer</h1>
              <p className="text-sm text-zinc-500">Draft personalized emails for {lead?.first_name} {lead?.last_name}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-7 space-y-6">
            {/* Lead Card with Generate Button */}
            <div className="bg-[#121820] rounded-2xl border border-white/10 p-6">
              <div className="flex items-start gap-6">
                {/* Lead Info */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500/30 to-pink-500/30 flex items-center justify-center">
                    <User className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-lg font-bold text-white">{lead?.first_name} {lead?.last_name}</h2>
                      {lead?.ai_score && (
                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded border border-cyan-500/30">
                          Score {lead.ai_score}
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-sm">{lead?.job_title || "Contact"} @ {lead?.company_name || "Unknown"}</p>
                    <p className="text-blue-400 text-sm mt-1">{lead?.email || "No email"}</p>
                  </div>
                </div>

                {/* Pain Points */}
                <div className="flex-1">
                  <p className="text-xs text-zinc-500 mb-1">Pain:</p>
                  <p className="text-sm text-zinc-300">{lead?.pain_points || lead?.notes || "No pain points identified"}</p>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={generateEmail}
                  disabled={generating || !lead?.email}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6 py-6 rounded-xl text-base"
                >
                  {generating ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-5 h-5 mr-2" />
                  )}
                  Generate Email
                </Button>
              </div>
            </div>

            {/* Email Template Selection */}
            <div>
              <p className="text-xs text-zinc-500 uppercase font-medium tracking-wider mb-4">Email Template</p>
              <div className="flex flex-wrap gap-3">
                {emailTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                      selectedTemplate === template.id
                        ? template.color + " border-2"
                        : "bg-[#121820] border-white/10 text-zinc-400 hover:border-white/20"
                    }`}
                  >
                    <span>{template.icon}</span>
                    <span className={selectedTemplate === template.id ? "" : "text-white"}>{template.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Email Preview Card */}
            <div className="bg-[#121820] rounded-2xl border border-white/10 overflow-hidden">
              {/* TO Field */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                <span className="text-zinc-500 text-sm uppercase font-medium">To</span>
                <span className="text-blue-400">{lead?.email || "No email"}</span>
              </div>

              {/* Subject Field */}
              <div className="flex items-center gap-4 px-6 py-4 border-b border-white/5">
                <span className="text-zinc-500 text-sm uppercase font-medium">Subject</span>
                <span className={currentEmail ? "text-white" : "text-zinc-600 italic"}>
                  {currentEmail?.subject || "Generate an email to see subject line"}
                </span>
              </div>

              {/* Email Body */}
              <div className="px-6 py-8 min-h-[300px]">
                {currentEmail ? (
                  <div className="space-y-4">
                    <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed">{currentEmail.body}</p>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-6 border-t border-white/5">
                      <Button
                        onClick={copyEmail}
                        variant="outline"
                        className="border-white/10 text-zinc-400 hover:bg-white/5"
                      >
                        {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                      <Button
                        onClick={openInGmail}
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Gmail
                      </Button>
                      <Button
                        onClick={openInOutlook}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Outlook
                      </Button>
                      <Button
                        onClick={generateEmail}
                        disabled={generating}
                        variant="outline"
                        className="border-white/10 text-zinc-400 hover:bg-white/5 ml-auto"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
                        Regenerate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-4">
                      <Mail className="w-8 h-8 text-purple-400/50" />
                    </div>
                    <p className="text-zinc-500">Select a template and click Generate Email</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Statistics */}
          <div className="col-span-5 space-y-6">
            {/* Email Statistics */}
            <div className="bg-[#121820] rounded-2xl border border-white/10 p-6">
              <p className="text-xs text-zinc-500 uppercase font-medium tracking-wider mb-4">Email Statistics</p>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#0a0f14] rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-white">{getDraftedCount()}</p>
                  <p className="text-xs text-zinc-500 mt-1">Drafted</p>
                </div>
                <div className="bg-[#0a0f14] rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-cyan-400">{getSentCount()}</p>
                  <p className="text-xs text-zinc-500 mt-1">Sent</p>
                </div>
                <div className="bg-[#0a0f14] rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{getOpenedCount()}</p>
                  <p className="text-xs text-zinc-500 mt-1">Opened</p>
                </div>
                <div className="bg-[#0a0f14] rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{getOpenRate()}%</p>
                  <p className="text-xs text-zinc-500 mt-1">Open Rate</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-6 border-b border-white/10 mb-4">
                {["drafts", "tips", "sequences"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-3 text-sm font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? "text-blue-400 border-b-2 border-blue-400"
                        : "text-zinc-500 hover:text-white"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === "drafts" && (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {emailHistory.length > 0 ? (
                    emailHistory.map((email, idx) => (
                      <div 
                        key={idx} 
                        className="bg-[#0a0f14] rounded-xl p-4 border border-white/5 cursor-pointer hover:border-blue-500/30 transition-all"
                        onClick={() => setCurrentEmail({
                          subject: email.subject,
                          body: email.body,
                          to: lead?.email,
                          generated_at: email.generated_at
                        })}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-zinc-400 text-sm">{formatDate(email.generated_at)}</p>
                          <div className="flex gap-2">
                            {email.status === "sent" && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-orange-500/20 text-orange-400">
                                Sent
                              </span>
                            )}
                            {email.opened && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-500/20 text-green-400">
                                Opened
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-cyan-400 font-medium mb-1">{email.subject || "No subject"}</p>
                        <p className="text-sm text-zinc-500 line-clamp-2">
                          {email.body?.substring(0, 100) || email.preview || "No preview available"}...
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Mail className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                      <p className="text-sm text-zinc-500">No emails drafted yet</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "tips" && (
                <div className="space-y-3">
                  <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
                    <p className="text-sm text-blue-300">
                      <span className="font-medium text-blue-400">Subject lines:</span> Keep them under 50 characters for better open rates.
                    </p>
                  </div>
                  <div className="bg-[#0a0f14] rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">Personalization:</span> Reference their specific pain points for higher engagement.
                    </p>
                  </div>
                  <div className="bg-[#0a0f14] rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">Best send times:</span> Tuesday-Thursday, 9-11 AM typically see highest open rates.
                    </p>
                  </div>
                  <div className="bg-[#0a0f14] rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">Follow-up:</span> Send a follow-up 3-5 days after the initial email if no response.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === "sequences" && (
                <div className="space-y-3">
                  <div className="bg-[#0a0f14] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Cold Outreach Sequence</p>
                      <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded">Active</span>
                    </div>
                    <p className="text-sm text-zinc-500">3 emails over 10 days</p>
                    <div className="flex gap-2 mt-3">
                      <div className="flex-1 h-1 bg-green-500 rounded" />
                      <div className="flex-1 h-1 bg-zinc-700 rounded" />
                      <div className="flex-1 h-1 bg-zinc-700 rounded" />
                    </div>
                  </div>
                  <div className="bg-[#0a0f14] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Demo Follow-up</p>
                      <span className="text-xs text-zinc-400 bg-zinc-500/20 px-2 py-0.5 rounded">Paused</span>
                    </div>
                    <p className="text-sm text-zinc-500">2 emails over 5 days</p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-white/10 text-zinc-400 hover:bg-white/5 mt-2"
                  >
                    + Create New Sequence
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
